import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useOrderContext from '../../contexts/order/useOrderContext';
import useBasketContext from '../../contexts/basket/useBasketContext';
import { Timeslot } from '../../contexts/site/site-context';
import useSiteContext from '../../contexts/site/useSiteContext';
import { getDisplayName } from '../../services/channel';
import { useLocalStorage } from "../../hooks/localstorage";
import { iLog } from "../../index";
import PageSpinnerThree from "../general/PageSpinnerThree";

type TimeslotSelectProps = {
    timeslots: Timeslot[] | null;
    disabledSelect?: boolean;
    selectedTimeslotId?: string | null;
    onChange?: () => void;
};

type GroupedTimeslots = {
    [date: string]: Timeslot[];
};

type TimeslotDayMapping = {
    [date: string]: string;
}

const TimeslotSelectTwoPart = ({ timeslots, selectedTimeslotId = null, disabledSelect = false, onChange = () => {} }: TimeslotSelectProps) => {
    const orderContext = useOrderContext();
    const basketContext = useBasketContext();
    const siteContext = useSiteContext();

    const [loading, setLoading] = useState(false);
    const [timeslotsByDay, setTimeslotsByDay] = useState<GroupedTimeslots|null>(null);
    const [timeslotDayNames, setTimeslotDayNames] = useState<TimeslotDayMapping>({});
    const [selectedTimeslotDate, setSelectedTimeslotDate] = useState<string|null>(null);
    const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot|null>(null);
    const [storedBasket, setStoredBasket] = useLocalStorage('basket', null);

    useEffect(() => {
        if ( siteContext.site && !siteContext.timeslots?.length ) {
            iLog('timeslot useEffect - calling fetch timeslot', [siteContext.site, basketContext.validatedSite]);

            setLoading(true);

            siteContext.fetchTimeslots(
                siteContext.site?.uid ?? basketContext.validatedSite?.uid ?? '',
                siteContext.flow ?? 'noflow');

            setLoading(false);
        }
    }, [siteContext.site]);

    useEffect(() => {
        if ( timeslots?.length ) {
            // Group timeslots by day - ASAP is grouped into "Today"
            let groupedTimeslots = timeslots.reduce((acc, timeslot) => {
                let newTimeslot = { ...timeslot };
                let date = newTimeslot.id?.split(' ')?.[0];

                if (newTimeslot.is_asap) {
                    handleTimeslotDayName(date, 'Today');
                } else {
                    let spaceIndex = newTimeslot.name?.lastIndexOf(' ');
                    let dayName = newTimeslot.name?.substr(0, spaceIndex);
                    let timeName = newTimeslot.name?.substr(spaceIndex + 1);
                    newTimeslot.name = timeName;
                    handleTimeslotDayName(date, dayName);
                }

                acc[date] = acc[date] || [];
                acc[date].push(newTimeslot);
                return acc;
            }, Object.create(null));

            setTimeslotsByDay(groupedTimeslots);
        } else {
            // Remove all stored timeslots and day names
            setTimeslotsByDay(null);
            setTimeslotDayNames({});
        }

        // Reset the dropdowns since the timeslots have potentially changed
        handleTimeslotDateSelected(undefined);
        handleTimeslotSelected(undefined);
    }, [timeslots]);

    useEffect(() => {
        if ( selectedTimeslotId && timeslots?.length ) {
            let date = selectedTimeslotId.split(' ')?.[0];
            let found = timeslots?.find((t) => selectedTimeslotId === t.id);
            iLog('Setting timeslot date', date);
            iLog('Selecting timeslot from storage', found);
            handleTimeslotDateSelected(date);
            handleTimeslotSelected(found);
        }
    }, [timeslots, selectedTimeslotId]);

    const handleTimeslotDayName = (date: string, dayName: string) => {
        if ( typeof timeslotDayNames[date] === 'string' && timeslotDayNames[date] !== '' ) {
            return;
        }

        let names = timeslotDayNames;
        names[date] = dayName;
        setTimeslotDayNames(names);
    };

    const handleTimeslotDateSelect = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        handleTimeslotDateSelected(e.target.value);
    };

    const handleTimeslotSelect = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        handleTimeslotSelected(timeslots?.find((t) => e.target.value === t.id));
    };

    const handleTimeslotDateSelected = (date: string | undefined) => {
        iLog('Setting timeslot date', date);

        setSelectedTimeslotDate(date || null);
        setSelectedTimeslot(null);

        basketContext.setTimeslot(null);
        setStoredBasket({
            ...(storedBasket ?? {}),
            timeslot: null
        });
    };

    const handleTimeslotSelected = (timeslot: Timeslot | undefined) => {
        iLog('Attempting to set timeslot to basketContext', timeslot);

        setSelectedTimeslot(timeslot || null);

        if (timeslot) {
            basketContext.setTimeslot(timeslot);

            setStoredBasket({
                ...(storedBasket ?? {}),
                timeslot: timeslot
            });

        } else {
            console.warn('No timeslot passed on timeslot select.');
        }
    };

    const renderTimeslotDateOptions = () => {
        if (!timeslotsByDay || Object.keys(timeslotsByDay)?.length === 0) {
            return <option key="ts-none" disabled={true} selected={true}>{t`No timeslots available`}</option>;
        }

        let renderedOptions = [];

        renderedOptions.push(
            <option key="ts-sel" value="">
                {t`Select date`}
            </option>,
        );

        renderedOptions.push(
            Object.keys(timeslotsByDay)?.map((day) => (
                <option
                    key={`day-${day}`}
                    value={day}
                >
                    {timeslotDayNames[day]}
                </option>
            )),
        );

        return renderedOptions;
    };

    const renderTimeslotOptions = () => {
        if (!selectedTimeslotDate) {
            return <option key="ts-none" disabled={true} selected={true}>{t`Select time`}</option>;
        }

        if (!timeslotsByDay || timeslotsByDay[selectedTimeslotDate]?.length === 0) {
            return <option key="ts-none" disabled={true} selected={true}>{t`No timeslots available`}</option>;
        }

        let renderedOptions = [];

        renderedOptions.push(
            <option key="ts-sel" value="">
                {t`Select time`}
            </option>,
        );

        renderedOptions.push(
            timeslotsByDay[selectedTimeslotDate]?.map((timeslot) => (
                <option
                    key={`ts-${timeslot.id}`}
                    value={timeslot.id}
                >
                    {timeslot.name}
                </option>
            )),
        );

        return renderedOptions;
    };

    return (
        <>
            <StyledDropdown>
                <div>
                    <Form.Control
                        value={selectedTimeslotDate || undefined}
                        onChange={(e) => { handleTimeslotDateSelect(e); onChange() }}
                        as="select"
                        disabled={disabledSelect}
                        data-testid="select-timeslot-date"
                    >
                        {renderTimeslotDateOptions()}
                    </Form.Control>
                </div>
                <span className="material-symbols-outlined chevron-down">keyboard_arrow_down</span>
                <StyledSpinnerOverlay show={loading}>
                    <PageSpinnerThree show={true}/>
                </StyledSpinnerOverlay>
            </StyledDropdown>
            <StyledDropdown>
                <div>
                    <Form.Control
                        value={selectedTimeslot?.id || undefined}
                        onChange={(e) => { handleTimeslotSelect(e); onChange() }}
                        as="select"
                        disabled={disabledSelect || !selectedTimeslotDate}
                        data-testid="select-timeslot"
                    >
                        {renderTimeslotOptions()}
                    </Form.Control>
                </div>
                <span className="material-symbols-outlined chevron-down">keyboard_arrow_down</span>
                <StyledSpinnerOverlay show={loading}>
                    <PageSpinnerThree show={true}/>
                </StyledSpinnerOverlay>
            </StyledDropdown>
        </>
    );
};

const StyledDropdown = styled.div`
    position: relative;
    border-radius: var(--radius_m);
    margin: 0;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }

    span.chevron-down {
        position: absolute;
        top: calc(50% - 11px);
        right: 16px;
        pointer-events: none;
        font-size: 24px;
        line-height: 24px;
    }

    .dropdown__label {
        font-size: 12px;
        line-height: 16px;
        display: block;
        position: absolute;
        top: 12px;
        left: 48px;
        pointer-events: none;
        user-select: none;
        display: block;
    }

    select {
        height: 48px;
        cursor: pointer;
        outline: none;
        box-shadow: 0 1px 2px 0 rgba(0 0 0 / 10%);
        transition: background-color 0.1s ease-in-out;

        &::-ms-expand {
          display: none;
        }

        -moz-appearance: none;
        -webkit-appearance: none;

        &:disabled {
            cursor: not-allowed;
            opacity: 1;
        }
    }
`;

const StyledSpinnerOverlay = styled.div<{show: boolean}>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transition: opacity 0.1s ease-in-out;
    opacity: ${(props) => props.show ? '1' : '0'};
    z-index: ${(props) => props.show ? '0' : '-1'};
    border-radius: var(--radius_l);
`;

export default TimeslotSelectTwoPart;
